// Central sign-out hand-off.
//
// Supabase's OAuth server exposes no `end_session_endpoint` (verified against
// the identity project's OIDC discovery document), so RP-initiated logout is
// not available. Clearing this portal's own Supabase session therefore leaves
// the central l2u-identity session alive, and the next sign-in is silent and
// credential-free.
//
// The workaround is a non-standard sign-out route on the central login app which
// clears ITS session and then returns here. This builds that URL.
//
// Returns null when unconfigured, which is the safe default: the caller stays
// local, so logout still ends the portal session rather than failing.
//
// VITE_IDENTITY_LOGOUT_URL is a BUILD-time value (Vite inlines it into the
// bundle), so it is supplied by the deploy pipeline's build step
// (pipelines/steps/deploy-app.yml) from the environment's DevOps variable group
// — not a Worker binding, and not anywhere it can be changed without a rebuild.
// Setting it requires a new build to take effect; re-running a deploy of an
// existing build will not pick it up. Unset, the symptom is silent: sign-out
// still ends the portal session and lands on /login, but the central session
// survives, so the next sign-in needs no credentials and returns the same
// identity. To confirm a build has it, grep the served bundle for the identity
// host.
//
// The central route this points at is l2u-identity's /logout. It only redirects
// back to origins in that app's own VITE_LOGOUT_ALLOWED_ORIGINS allow-list, so
// this portal's admin origin must appear there too.
export function buildIdentityLogoutUrl(
  logoutBase: string | undefined,
  returnTo: string,
  reason?: string,
): string | null {
  const base = (logoutBase ?? '').trim().replace(/\/+$/, '');
  if (!base) return null;
  // An unexpanded Azure DevOps token means the pipeline referenced a variable
  // the environment's variable group does not define, so the literal string
  // "$(VITE_IDENTITY_LOGOUT_URL)" was baked into the bundle. Treated as
  // unconfigured rather than as a base URL: the alternative is navigating the
  // browser to a nonsense address, which loses the portal sign-out that had
  // already succeeded. Unconfigured merely leaves the central session alive.
  // Not hypothetical here — the FedEx test cutover had to verify explicitly
  // that no $(VITE_...) literals reached the deployed bundles.
  if (base.startsWith('$(') && base.endsWith(')')) return null;
  const separator = base.includes('?') ? '&' : '?';
  const url = `${base}${separator}redirect_to=${encodeURIComponent(returnTo)}`;
  // redirect_to is always written first, so reason's separator is always '&'.
  return reason ? `${url}&reason=${encodeURIComponent(reason)}` : url;
}

// Whether a sign-out should hand off to the central l2u-identity /logout, or
// stop at clearing this portal's own session.
//
// The question being answered is "did this person authenticate through central
// identity?", and the honest answer is that the portal cannot know for certain
// at sign-out time. So this is deliberately asymmetric: it hands off unless it
// can positively identify a password session on a non-SSO domain.
//
// That direction is chosen from how the two failures behave. A redundant
// hand-off is a no-op redirect the user sees and shrugs at. A MISSED hand-off
// is silent: the central session survives, the next sign-in is credential-free,
// no new SAML assertion is issued, and the user keeps re-spending whatever Okta
// group claims were captured at their original sign-in -- for as long as the
// central session is refreshed. That failure is invisible from inside the
// portal and cost real time to diagnose once already.
//
// NOT `access_source`. That column says how a user is AUTHORISED, not how they
// authenticated, and the two are deliberately independent here. In compliance
// TEST, 8 of 9 fedex.com users are access_source='local' and every one of them
// signs in through central identity -- keying on it would skip the hand-off for
// almost exactly the population that needs it.
//
// The domain is the right signal because it is the SAME one that decides
// whether the SSO button is offered at all (VITE_ADMIN_SSO_DOMAIN, AdminLogin).
// If SSO was offered to this address, central sign-in is what they used.
export function shouldHandOffToIdentity({
  ssoDomain,
  email,
  provider,
}: {
  ssoDomain: string | undefined;
  email: string | null | undefined;
  provider: string | null | undefined;
}): boolean {
  // Same `$(VAR)` guard as buildIdentityLogoutUrl's caller contract: Azure
  // DevOps bakes the literal token in for a variable its group does not define,
  // and that string is truthy. Treated as unconfigured rather than as a domain
  // no address can match.
  const raw = (ssoDomain ?? '').trim();
  const configured = raw && !(raw.startsWith('$(') && raw.endsWith(')')) ? raw.toLowerCase() : null;

  const emailDomain = (email ?? '').split('@')[1]?.trim().toLowerCase() || null;
  if (configured && emailDomain === configured) return true;

  // 'email' is Supabase's provider for password sign-in. Only this one value
  // is trusted to mean "no central session"; anything else -- absent, unknown,
  // or a provider added later -- hands off.
  return provider !== 'email';
}
